import React, { useCallback, useEffect, useState } from "react";
import { api, inr, imageUrl, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ADMIN } from "@/constants/testIds";
import { ChevronLeft, ChevronRight, Filter, Search, X, ChevronDown, ChevronUp, Truck, Package, Sparkles, Home, Clock, Ban, CheckCircle2, MapPin, Share2, ExternalLink, Pencil, Save, Trash2, Printer, Plus } from "lucide-react";

const STATUSES = [
  { key: "all", label: "All", color: "text-hyd-text", chip: "border-hyd-border" },
  { key: "awaiting", label: "Awaiting", color: "text-hyd-gold", chip: "border-hyd-gold/60 bg-hyd-gold/10" },
  { key: "confirmed", label: "Confirmed", color: "text-blue-300", chip: "border-blue-500/50 bg-blue-500/10" },
  { key: "packed", label: "Packed", color: "text-blue-300", chip: "border-blue-500/50 bg-blue-500/10" },
  { key: "dispatched", label: "Dispatched", color: "text-indigo-300", chip: "border-indigo-500/50 bg-indigo-500/10" },
  { key: "delivered", label: "Delivered", color: "text-emerald-300", chip: "border-emerald-500/50 bg-emerald-500/10" },
  { key: "cancelled", label: "Cancelled", color: "text-hyd-red", chip: "border-hyd-red/50 bg-hyd-red/10" },
];
const NEXT_STATUS = ["awaiting", "confirmed", "packed", "dispatched", "delivered", "cancelled"];

const PAGE_SIZE = 10;

export default function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({
    status: "awaiting",
    order_number: "",
    phone: "",
    product: "",
    date_from: "",
    date_to: "",
  });
  const [expanded, setExpanded] = useState(null); // order id

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, page_size: PAGE_SIZE };
      Object.keys(params).forEach((k) => { if (params[k] === "" || params[k] == null) delete params[k]; });
      const [rowsRes, countsRes] = await Promise.all([
        api.get("/admin/orders", { params }),
        api.get("/admin/orders/status-counts"),
      ]);
      setRows(rowsRes.data.items);
      setTotal(rowsRes.data.total);
      setCounts(countsRes.data);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Order ${status}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const setF = (k, v) => { setPage(1); setFilters((f) => ({ ...f, [k]: v })); };
  const clearFilters = () => { setPage(1); setFilters({ status: "all", order_number: "", phone: "", product: "", date_from: "", date_to: "" }); };
  const hasFilters = Object.values(filters).some((v) => v && v !== "all");

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="min-w-0 w-full max-w-full">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Orders</h1>
          <p className="text-hyd-text2 text-sm">{counts.all || 0} total · {counts.awaiting || 0} awaiting confirmation</p>
        </div>
        {hasFilters && (
          <Button variant="ghost" onClick={clearFilters} className="text-hyd-red hover:bg-hyd-panel rounded-full">
            <X className="w-4 h-4 mr-1" /> Clear filters
          </Button>
        )}
      </div>

      {/* Status filter chips */}
      <div className="mt-4 flex flex-wrap gap-2 min-w-0">
        {STATUSES.map((s) => {
          const active = filters.status === s.key;
          const count = counts[s.key] || 0;
          return (
            <button
              key={s.key}
              data-testid={ADMIN.orderStatusChip(s.key)}
              onClick={() => setF("status", s.key)}
              className={`shrink-0 px-3 py-2 rounded-2xl border text-xs font-bold uppercase tracking-wider transition-colors flex items-center gap-2 ${
                active ? `${s.chip} ${s.color}` : "border-hyd-border bg-hyd-panel text-hyd-text2 hover:text-hyd-gold"
              }`}
            >
              <span>{s.label}</span>
              <span className={`min-w-[22px] h-5 grid place-items-center rounded-full text-[10px] ${active ? "bg-hyd-bg text-hyd-text" : "bg-hyd-bg text-hyd-text2"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search filters */}
      <div className="mt-4 grid min-w-0 grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
        <FilterField icon={Search} label="Order #">
          <Input data-testid={ADMIN.orderFilterNumber} value={filters.order_number} onChange={(e) => setF("order_number", e.target.value)} placeholder="HYD..." />
        </FilterField>
        <FilterField icon={Search} label="Phone">
          <Input data-testid={ADMIN.orderFilterPhone} value={filters.phone} onChange={(e) => setF("phone", e.target.value)} placeholder="9999" />
        </FilterField>
        <FilterField icon={Search} label="Product name">
          <Input data-testid={ADMIN.orderFilterProduct} value={filters.product} onChange={(e) => setF("product", e.target.value)} placeholder="Sparklers" />
        </FilterField>
        <FilterField icon={Filter} label="From date">
          <Input data-testid={ADMIN.orderFilterFrom} type="date" value={filters.date_from} onChange={(e) => setF("date_from", e.target.value)} />
        </FilterField>
        <FilterField icon={Filter} label="To date">
          <Input data-testid={ADMIN.orderFilterTo} type="date" value={filters.date_to} onChange={(e) => setF("date_to", e.target.value)} />
        </FilterField>
      </div>

      {/* Table */}
      <div className="mt-4 min-w-0 w-full bg-hyd-panel border border-hyd-border rounded-2xl sm:rounded-3xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-hyd-text2">Loading...</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-hyd-text3">No orders match these filters.</div>
        ) : (
          <div className="p-2">
              <div className="hidden lg:grid lg:grid-cols-[minmax(90px,1.05fr)_minmax(95px,1.1fr)_minmax(95px,1.1fr)_minmax(45px,.5fr)_minmax(75px,.8fr)_minmax(60px,.65fr)_minmax(85px,.85fr)_28px_36px] gap-2 px-3 py-2 text-[10px] uppercase tracking-wider text-hyd-text3">
                <span>Order ID</span>
                <span>Name</span>
                <span>Phone</span>
                <span>Items</span>
                <span>Price</span>
                <span>Pincode</span>
                <span>Status</span>
                <span aria-hidden="true" />
                <span className="text-center">Print</span>
              </div>
              <div className="space-y-2">
                {rows.map((o) => {
                  const open = expanded === o.id;
                  return (
                    <div key={o.id} data-testid={ADMIN.orderRow(o.id)} className="grid grid-cols-2 lg:grid-cols-[minmax(90px,1.05fr)_minmax(95px,1.1fr)_minmax(95px,1.1fr)_minmax(45px,.5fr)_minmax(75px,.8fr)_minmax(60px,.65fr)_minmax(85px,.85fr)_28px_36px] gap-x-3 gap-y-3 lg:gap-2 items-center rounded-xl border border-hyd-border p-3">
                      <div className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Order ID</span>
                        <span className="block font-mono text-xs text-hyd-text2 break-all">{o.order_number}</span>
                        <span className="block text-[10px] text-hyd-text3">{new Date(o.created_at).toLocaleString()}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Name</span>
                        <span className="block text-sm font-medium truncate">{o.user_name || o.address?.name || "—"}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Phone</span>
                        <span className="block text-sm text-hyd-text2 truncate">{o.user_phone || o.address?.phone || "—"}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Items</span>
                        <span className="text-sm text-hyd-text2">{o.items?.length ?? 0}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Price</span>
                        <span className="text-sm font-bold text-hyd-gold">{inr(o.total)}</span>
                      </div>
                      <div>
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Pincode</span>
                        <span className="text-sm text-hyd-text2">{o.address?.pincode || "—"}</span>
                      </div>
                      <div className="min-w-0">
                        <span className="block text-[10px] uppercase tracking-wider text-hyd-text3 lg:hidden">Status</span>
                        <StatusBadge status={o.status} />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`${open ? "Collapse" : "Expand"} order ${o.order_number}`}
                        aria-expanded={open}
                        onClick={() => setExpanded(open ? null : o.id)}
                        className="h-9 w-9 justify-self-start lg:justify-self-center text-hyd-text2 hover:text-hyd-gold"
                      >
                        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`Print order ${o.order_number}`}
                        title="Print order"
                        onClick={() => printOrderInvoice(o)}
                        className="h-9 w-9 justify-self-end lg:justify-self-center text-hyd-text2 hover:text-hyd-gold"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      {open && (
                        <div className="col-span-2 lg:col-span-9">
                          <OrderDetailPanel order={o} canEdit={["awaiting", "confirmed"].includes(String(o.status).toLowerCase())} onStatus={changeStatus} onSaved={load} />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      <div className="mt-4 min-w-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-xs text-hyd-text2 text-center sm:text-left">
          Showing {rows.length ? (page - 1) * PAGE_SIZE + 1 : 0}–{(page - 1) * PAGE_SIZE + rows.length} of {total}
        </div>
        <div className="min-w-0 flex items-center justify-between sm:justify-end gap-2">
          <Button data-testid={ADMIN.orderPagePrev} onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            <ChevronLeft className="w-4 h-4" /> Prev
          </Button>
          <div className="text-xs text-hyd-text2 min-w-[80px] text-center">Page {page} of {totalPages}</div>
          <Button data-testid={ADMIN.orderPageNext} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} variant="secondary" className="bg-hyd-panel border border-hyd-border text-hyd-text2 rounded-full">
            Next <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function printOrderInvoice(order) {
  const invoiceWindow = window.open("", "_blank");
  if (!invoiceWindow) {
    toast.error("Allow pop-ups to print or save this invoice.");
    return;
  }
  invoiceWindow.opener = null;

  const escape = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[char]);
  const address = order.address || {};
  const itemRows = (order.items || []).map((item) => `
    <tr>
      <td>${escape(item.name)}</td>
      <td>${escape(item.kind)}</td>
      <td class="number">${Number(item.qty) || 0}</td>
      <td class="number">${escape(inr(item.unit_price))}</td>
      <td class="number">${escape(inr((Number(item.qty) || 0) * (Number(item.unit_price) || 0)))}</td>
    </tr>
  `).join("");
  const discountRow = Number(order.discount) > 0
    ? `<div class="total-row"><span>Discount${order.coupon_code ? ` (${escape(order.coupon_code)})` : ""}</span><span>- ${escape(inr(order.discount))}</span></div>`
    : "";

  invoiceWindow.document.open();
  invoiceWindow.document.write(`<!doctype html>
    <html><head><meta charset="utf-8"><title>Invoice ${escape(order.order_number)}</title>
    <style>
      *{box-sizing:border-box}body{font:14px Arial,sans-serif;color:#171717;margin:0;padding:36px}
      .invoice{max-width:850px;margin:0 auto}.top{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #b45309;padding-bottom:18px}
      h1{font-size:24px;margin:0 0 6px}.muted{color:#666;font-size:12px}.meta{text-align:right}
      .addresses{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin:24px 0}.label{font-size:10px;text-transform:uppercase;color:#666;font-weight:bold;margin-bottom:6px}
      .address{line-height:1.6}.items{width:100%;border-collapse:collapse;margin-top:18px}.items th,.items td{border-bottom:1px solid #ddd;padding:10px 8px;text-align:left}.items th{font-size:10px;text-transform:uppercase;color:#555;background:#f5f5f5}.number{text-align:right!important;white-space:nowrap}
      .totals{width:min(320px,100%);margin:18px 0 0 auto}.total-row{display:flex;justify-content:space-between;padding:5px 0}.grand{border-top:2px solid #222;margin-top:6px;padding-top:10px;font-size:18px;font-weight:bold}.notes{margin-top:24px;white-space:pre-wrap}
      @media print{body{padding:0}.invoice{max-width:none}.no-print{display:none}}
      @media(max-width:560px){body{padding:18px}.top{gap:12px}.addresses{gap:12px}}
    </style></head><body><main class="invoice">
      <header class="top"><div><h1>Hyderabad Crackers</h1><div class="muted">Order invoice</div></div>
        <div class="meta"><strong>Invoice ${escape(order.order_number)}</strong><div class="muted">${escape(new Date(order.created_at).toLocaleString())}</div><div class="muted">Status: ${escape(order.status)}</div></div></header>
      <section class="addresses"><div><div class="label">Customer</div><div class="address"><strong>${escape(order.user_name || address.name)}</strong><br>${escape(order.user_phone || address.phone)}</div></div>
        <div><div class="label">Shipping address</div><div class="address">${escape(address.name)}<br>${escape(address.line1)}${address.line2 ? `<br>${escape(address.line2)}` : ""}<br>${escape(address.city)}, ${escape(address.state)} ${escape(address.pincode)}${address.landmark ? `<br>Landmark: ${escape(address.landmark)}` : ""}</div></div></section>
      <table class="items"><thead><tr><th>Product</th><th>Type</th><th class="number">Qty</th><th class="number">Unit price</th><th class="number">Amount</th></tr></thead><tbody>${itemRows}</tbody></table>
      <section class="totals"><div class="total-row"><span>Subtotal</span><span>${escape(inr(order.subtotal))}</span></div><div class="total-row"><span>Delivery</span><span>${Number(order.delivery_fee) === 0 ? "FREE" : escape(inr(order.delivery_fee))}</span></div>${discountRow}<div class="total-row grand"><span>Total</span><span>${escape(inr(order.total))}</span></div><div class="muted">Payment: ${escape(order.payment_method)}</div></section>
      ${order.notes ? `<section class="notes"><div class="label">Notes</div>${escape(order.notes)}</section>` : ""}
    </main><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));</script></body></html>`);
  invoiceWindow.document.close();
}

function FilterField({ icon: Icon, label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest mb-1 flex items-center gap-1">
        <Icon className="w-3 h-3 text-hyd-gold" /> {label}
      </Label>
      <div className="[&_input]:bg-hyd-bg [&_input]:border-hyd-border [&_input]:text-hyd-text [&_input]:h-9 [&_input]:rounded-xl">
        {children}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    awaiting:   "bg-hyd-gold/20 text-hyd-gold border-hyd-gold/50",
    confirmed:  "bg-blue-500/15 text-blue-300 border-blue-500/40",
    packed:     "bg-blue-500/15 text-blue-300 border-blue-500/40",
    dispatched: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
    delivered:  "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    cancelled:  "bg-hyd-red/15 text-hyd-red border-hyd-red/40",
  };
  return (
    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-full border ${map[status] || "border-hyd-border"}`}>
      {status}
    </span>
  );
}

function OrderDetailPanel({ order, canEdit, onStatus, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponError, setCouponError] = useState("");
  const [checkingCoupon, setCheckingCoupon] = useState(false);
  const [couponEntryOpen, setCouponEntryOpen] = useState(false);
  const [couponValidatedSubtotal, setCouponValidatedSubtotal] = useState(null);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearch, setProductSearch] = useState("");
  const [productResults, setProductResults] = useState([]);
  const [productSearchLoading, setProductSearchLoading] = useState(false);

  useEffect(() => {
    const query = productSearch.trim();
    if (!editing || !productSearchOpen || !query) {
      setProductResults([]);
      setProductSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setProductSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const response = await api.get("/products", { params: { q: query, limit: 20 } });
        if (!cancelled) setProductResults(response.data);
      } catch {
        if (!cancelled) {
          setProductResults([]);
          toast.error("Unable to search products");
        }
      } finally {
        if (!cancelled) setProductSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [editing, productSearch, productSearchOpen]);

  const beginEdit = () => {
    if (!canEdit) return;
    setProductSearchOpen(false);
    setProductSearch("");
    const currentCoupon = order.coupon_code || "";
    setCouponCode(currentCoupon);
    setCouponApplied(currentCoupon ? { code: currentCoupon, discount: Number(order.discount) || 0 } : null);
    setCouponError("");
    setCouponEntryOpen(false);
    setCouponValidatedSubtotal(Number(order.subtotal) || 0);
    setDraft({
      items: order.items.map((item) => ({ ...item })),
      address: { ...order.address },
      delivery_fee: order.delivery_fee ?? 0,
      notes: order.notes || "",
      coupon_code: currentCoupon,
      coupon_discount: Number(order.discount) || 0,
    });
    setEditing(true);
  };

  const addProduct = (product) => {
    const image = product.images?.[product.primary_image_index || 0] || product.images?.[0] || "";
    setDraft((current) => {
      const existingIndex = current.items.findIndex((item) => item.kind === "product" && item.ref_id === product.id);
      if (existingIndex >= 0) {
        return {
          ...current,
          items: current.items.map((item, index) => index === existingIndex ? { ...item, qty: Number(item.qty) + 1 } : item),
        };
      }
      return {
        ...current,
        items: [...current.items, {
          kind: "product",
          ref_id: product.id,
          name: product.name,
          unit_price: Number(product.price),
          qty: 1,
          image_url: image,
        }],
      };
    });
    setProductSearch("");
    setProductResults([]);
  };

  const updateItem = (index, key, value) => {
    setDraft((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, [key]: value } : item),
    }));
  };

  const updateAddress = (key, value) => {
    setDraft((current) => ({ ...current, address: { ...current.address, [key]: value } }));
  };

  const subtotal = editing
    ? draft.items.reduce((sum, item) => sum + (Number(item.unit_price) || 0) * (Number(item.qty) || 0), 0)
    : order.subtotal;
  const discount = editing
    ? Math.min(Math.max(Number(draft.coupon_discount) || 0, 0), subtotal)
    : Math.min(Math.max(Number(order.discount) || 0, 0), subtotal);
  const deliveryFee = editing ? (Number(draft.delivery_fee) || 0) : order.delivery_fee;
  const total = Math.round((subtotal + deliveryFee - discount) * 100) / 100;

  useEffect(() => {
    if (!editing || !draft?.coupon_code || subtotal === couponValidatedSubtotal) return undefined;
    let active = true;
    setCheckingCoupon(true);
    setCouponError("");
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.post("/coupons/validate", {
          code: draft.coupon_code,
          subtotal,
        });
        if (!active) return;
        setCouponApplied(data);
        setCouponValidatedSubtotal(subtotal);
        setDraft((current) => current?.coupon_code === data.code
          ? { ...current, coupon_discount: Number(data.discount) || 0 }
          : current);
      } catch (error) {
        if (!active) return;
        setCouponError(formatApiErrorDetail(error.response?.data?.detail) || "Unable to validate coupon");
        setCouponApplied(null);
        setCouponValidatedSubtotal(subtotal);
        setDraft((current) => current?.coupon_code === draft.coupon_code
          ? { ...current, coupon_discount: 0 }
          : current);
      } finally {
        if (active) setCheckingCoupon(false);
      }
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [couponValidatedSubtotal, draft?.coupon_code, editing, subtotal]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    if (!code) return;
    setCheckingCoupon(true);
    setCouponError("");
    try {
      const { data } = await api.post("/coupons/validate", { code, subtotal });
      setDraft((current) => ({
        ...current,
        coupon_code: data.code,
        coupon_discount: Number(data.discount) || 0,
      }));
      setCouponCode(data.code);
      setCouponApplied(data);
      setCouponValidatedSubtotal(subtotal);
      setCouponEntryOpen(false);
      toast.success(`Coupon applied — you save ${inr(data.discount)}`);
    } catch (error) {
      setCouponError(formatApiErrorDetail(error.response?.data?.detail) || "Unable to validate coupon");
    } finally {
      setCheckingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setDraft((current) => ({ ...current, coupon_code: "", coupon_discount: 0 }));
    setCouponCode("");
    setCouponApplied(null);
    setCouponError("");
    setCouponEntryOpen(false);
    setCouponValidatedSubtotal(subtotal);
  };

  const printInvoice = () => {
    const invoiceWindow = window.open("", "_blank");
    if (!invoiceWindow) {
      toast.error("Allow pop-ups to print or save this invoice.");
      return;
    }
    invoiceWindow.opener = null;

    const escape = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    })[char]);
    const address = order.address || {};
    const itemRows = (order.items || []).map((item) => `
      <tr>
        <td>${escape(item.name)}</td>
        <td>${escape(item.kind)}</td>
        <td class="number">${Number(item.qty) || 0}</td>
        <td class="number">${escape(inr(item.unit_price))}</td>
        <td class="number">${escape(inr((Number(item.qty) || 0) * (Number(item.unit_price) || 0)))}</td>
      </tr>
    `).join("");
    const discountRow = Number(order.discount) > 0
      ? `<div class="total-row"><span>Discount${order.coupon_code ? ` (${escape(order.coupon_code)})` : ""}</span><span>- ${escape(inr(order.discount))}</span></div>`
      : "";

    invoiceWindow.document.open();
    invoiceWindow.document.write(`<!doctype html>
      <html><head><meta charset="utf-8"><title>Invoice ${escape(order.order_number)}</title>
      <style>
        *{box-sizing:border-box}body{font:14px Arial,sans-serif;color:#171717;margin:0;padding:36px}
        .invoice{max-width:850px;margin:0 auto}.top{display:flex;justify-content:space-between;gap:24px;border-bottom:2px solid #b45309;padding-bottom:18px}
        h1{font-size:24px;margin:0 0 6px}.muted{color:#666;font-size:12px}.meta{text-align:right}
        .addresses{display:grid;grid-template-columns:1fr 1fr;gap:24px;margin:24px 0}.label{font-size:10px;text-transform:uppercase;color:#666;font-weight:bold;margin-bottom:6px}
        .address{line-height:1.6}.items{width:100%;border-collapse:collapse;margin-top:18px}.items th,.items td{border-bottom:1px solid #ddd;padding:10px 8px;text-align:left}.items th{font-size:10px;text-transform:uppercase;color:#555;background:#f5f5f5}.number{text-align:right!important;white-space:nowrap}
        .totals{width:min(320px,100%);margin:18px 0 0 auto}.total-row{display:flex;justify-content:space-between;padding:5px 0}.grand{border-top:2px solid #222;margin-top:6px;padding-top:10px;font-size:18px;font-weight:bold}.notes{margin-top:24px;white-space:pre-wrap}
        @media print{body{padding:0}.invoice{max-width:none}.no-print{display:none}}
        @media(max-width:560px){body{padding:18px}.top{gap:12px}.addresses{gap:12px}}
      </style></head><body><main class="invoice">
        <header class="top"><div><h1>Hyderabad Crackers</h1><div class="muted">Order invoice</div></div>
          <div class="meta"><strong>Invoice ${escape(order.order_number)}</strong><div class="muted">${escape(new Date(order.created_at).toLocaleString())}</div><div class="muted">Status: ${escape(order.status)}</div></div></header>
        <section class="addresses"><div><div class="label">Customer</div><div class="address"><strong>${escape(order.user_name || address.name)}</strong><br>${escape(order.user_phone || address.phone)}</div></div>
          <div><div class="label">Shipping address</div><div class="address">${escape(address.name)}<br>${escape(address.line1)}${address.line2 ? `<br>${escape(address.line2)}` : ""}<br>${escape(address.city)}, ${escape(address.state)} ${escape(address.pincode)}${address.landmark ? `<br>Landmark: ${escape(address.landmark)}` : ""}</div></div></section>
        <table class="items"><thead><tr><th>Product</th><th>Type</th><th class="number">Qty</th><th class="number">Unit price</th><th class="number">Amount</th></tr></thead><tbody>${itemRows}</tbody></table>
        <section class="totals"><div class="total-row"><span>Subtotal</span><span>${escape(inr(order.subtotal))}</span></div><div class="total-row"><span>Delivery</span><span>${Number(order.delivery_fee) === 0 ? "FREE" : escape(inr(order.delivery_fee))}</span></div>${discountRow}<div class="total-row grand"><span>Total</span><span>${escape(inr(order.total))}</span></div><div class="muted">Payment: ${escape(order.payment_method)}</div></section>
        ${order.notes ? `<section class="notes"><div class="label">Notes</div>${escape(order.notes)}</section>` : ""}
      </main><script>window.addEventListener('load',()=>setTimeout(()=>window.print(),250));</script></body></html>`);
    invoiceWindow.document.close();
  };

  const saveOrder = async () => {
    if (!draft.items.length) {
      toast.error("An order must contain at least one product.");
      return;
    }
    const hasInvalidItem = draft.items.some((item) =>
      !Number.isInteger(Number(item.qty)) || Number(item.qty) < 1 ||
      !Number.isFinite(Number(item.unit_price)) || Number(item.unit_price) < 0
    );
    if (hasInvalidItem || !Number.isFinite(Number(draft.delivery_fee)) || Number(draft.delivery_fee) < 0) {
      toast.error("Check product quantities, prices, and delivery fee.");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/admin/orders/${order.id}`, {
        items: draft.items.map((item) => ({ ...item, qty: Number(item.qty), unit_price: Number(item.unit_price) })),
        address: draft.address,
        delivery_fee: Number(draft.delivery_fee),
        notes: draft.notes,
        coupon_code: draft.coupon_code || null,
      });
      toast.success("Order details updated");
      setEditing(false);
      setDraft(null);
      setProductSearchOpen(false);
      setProductSearch("");
      await onSaved?.();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Unable to update order");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={ADMIN.orderDetailPanel} className="bg-hyd-bg/60 border-t border-hyd-border px-4 py-5">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          {editing ? (
            <Button type="button" variant="ghost"             onClick={() => { setEditing(false); setDraft(null); setProductSearchOpen(false); setProductSearch(""); setCouponEntryOpen(false); setCouponError(""); }} disabled={saving} className="text-hyd-text2">
              Cancel
            </Button>
          ) : canEdit ? (
            <Button type="button" variant="secondary" onClick={beginEdit} className="bg-hyd-panel border border-hyd-border text-hyd-text2">
              <Pencil className="w-4 h-4 mr-1" /> Edit order
            </Button>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2">
          <Button type="button" onClick={printInvoice} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold">
            <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
          </Button>
          {editing && (
            <Button type="button" onClick={saveOrder} disabled={saving} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim font-bold">
              <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : "Save order"}
            </Button>
          )}
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {/* Items */}
        <div className="md:col-span-2">
          <SectionTitle>{editing ? draft.items.length : order.items.length} items</SectionTitle>
          {editing && (
            <div className="mt-2">
              {!productSearchOpen ? (
                <Button type="button" variant="secondary" onClick={() => setProductSearchOpen(true)} className="bg-hyd-panel border border-hyd-border text-hyd-gold">
                  <Plus className="w-4 h-4 mr-1" /> Add Products
                </Button>
              ) : (
                <div className="relative">
                  <div className="flex gap-2">
                    <Input
                      autoFocus
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      placeholder="Search products by name"
                      aria-label="Search products to add"
                      className="bg-hyd-panel border-hyd-border text-hyd-text"
                    />
                    <Button type="button" variant="ghost" onClick={() => { setProductSearchOpen(false); setProductSearch(""); }} className="text-hyd-text2">
                      <X className="w-4 h-4" /><span className="sr-only">Close product search</span>
                    </Button>
                  </div>
                  {productSearch.trim() && (
                    <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto rounded-xl border border-hyd-border bg-hyd-panel shadow-xl">
                      {productSearchLoading ? (
                        <div className="p-3 text-sm text-hyd-text2">Searching products...</div>
                      ) : productResults.length ? (
                        productResults.map((product) => (
                          <button
                            key={product.id}
                            type="button"
                            onClick={() => addProduct(product)}
                            className="w-full flex items-center justify-between gap-3 p-3 text-left hover:bg-hyd-panel2 border-b last:border-b-0 border-hyd-border"
                          >
                            <span className="min-w-0 truncate text-sm font-medium">{product.name}</span>
                            <span className="shrink-0 text-sm font-bold text-hyd-gold">{inr(product.price)}</span>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-sm text-hyd-text2">No matching products found.</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl divide-y divide-hyd-border">
            {(editing ? draft.items : order.items).map((item, index) => (
              <div
                key={`${item.ref_id}-${index}`}
                className={`p-3 grid gap-2 sm:gap-3 items-center ${editing
                  ? "grid-cols-[48px_minmax(0,1fr)_52px_32px] sm:grid-cols-[48px_minmax(0,1fr)_72px_110px_32px]"
                  : "grid-cols-[48px_minmax(0,1fr)_52px_auto] sm:grid-cols-[48px_minmax(0,1fr)_72px_110px]"
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-hyd-panel2 overflow-hidden shrink-0 border border-hyd-border">
                  {item.image_url && <img src={imageUrl(item.image_url)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold line-clamp-2">{item.name}</div>
                  {editing ? (
                    <label className="mt-1 block text-[10px] uppercase text-hyd-text2 tracking-widest">
                      Unit price
                      <Input type="number" min="0" step="0.01" value={item.unit_price} onChange={(e) => updateItem(index, "unit_price", e.target.value)} className="mt-1 h-8 bg-hyd-bg border-hyd-border text-hyd-text" />
                    </label>
                  ) : <div className="text-xs text-hyd-text3">{inr(item.unit_price)} each · {item.kind}</div>}
                </div>
                <div className="text-center">
                  {editing
                    ? <Input type="number" min="1" step="1" aria-label={`Quantity of ${item.name}`} value={item.qty} onChange={(e) => updateItem(index, "qty", e.target.value)} className="h-8 px-1 text-center bg-hyd-bg border-hyd-border text-hyd-text" />
                    : <div className="font-bold text-hyd-text">{item.qty}</div>}
                  <div className="text-[10px] uppercase text-hyd-text3 mt-0.5">Qty</div>
                </div>
                <div className="hidden sm:block text-right text-hyd-gold font-bold text-sm">{inr(Number(item.qty) * Number(item.unit_price))}</div>
                {editing ? (
                  <Button type="button" variant="ghost" size="icon" aria-label={`Remove ${item.name}`} onClick={() => setDraft((current) => ({ ...current, items: current.items.filter((_, itemIndex) => itemIndex !== index) }))} disabled={draft.items.length === 1 || saving} className="text-hyd-red">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                ) : <div className="sm:hidden text-right text-hyd-gold font-bold text-xs">{inr(Number(item.qty) * Number(item.unit_price))}</div>}
              </div>
            ))}
          </div>

          {(editing || order.notes) && (
            <div className="mt-3">
              <SectionTitle>Customer comments</SectionTitle>
              {editing
                ? <Textarea value={draft.notes} onChange={(e) => setDraft((current) => ({ ...current, notes: e.target.value }))} rows={3} className="mt-2 bg-hyd-panel border-hyd-border text-hyd-text" />
                : <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl p-3 text-sm text-hyd-text2 whitespace-pre-wrap">{order.notes}</div>}
            </div>
          )}

          <div className="mt-3">
            <SectionTitle>Status history</SectionTitle>
            <div className="mt-2 bg-hyd-panel border border-hyd-border rounded-2xl p-3 space-y-1 text-xs">
              {(order.status_history || []).map((h, idx) => (
                <div key={idx} className="flex justify-between text-hyd-text2">
                  <span className="uppercase tracking-widest font-bold text-hyd-text">{h.status}</span>
                  <span>{new Date(h.at).toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-3">
          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Shipping</SectionTitle>
            {editing ? (
              <div className="mt-2 grid grid-cols-2 gap-2">
                {[
                  ["name", "Recipient name", true],
                  ["phone", "Phone", false],
                  ["line1", "Address line 1", true],
                  ["line2", "Address line 2", true],
                  ["city", "City", false],
                  ["state", "State", false],
                  ["pincode", "Pincode", false],
                  ["landmark", "Landmark", true],
                ].map(([key, label, full]) => (
                  <label key={key} className={`text-[10px] uppercase text-hyd-text2 tracking-widest ${full ? "col-span-2" : ""}`}>
                    {label}
                    <Input value={draft.address[key] || ""} onChange={(e) => updateAddress(key, e.target.value)} className="mt-1 h-8 bg-hyd-bg border-hyd-border text-hyd-text" />
                  </label>
                ))}
              </div>
            ) : (
            <div className="mt-2 text-sm">
              <div className="font-semibold">{order.address.name}</div>
              <div className="text-hyd-text2">{order.address.line1}{order.address.line2 ? ", " + order.address.line2 : ""}</div>
              <div className="text-hyd-text2">{order.address.city}, {order.address.state} - {order.address.pincode}</div>
              <div className="text-hyd-text2 mt-1">📞 {order.address.phone}</div>
              {order.address.landmark && <div className="text-hyd-text3 text-xs mt-1">Landmark: {order.address.landmark}</div>}
              {order.delivery_slot && (
                <div className="mt-2 flex items-center gap-1 text-xs text-hyd-gold">
                  <Clock className="w-3.5 h-3.5" /> {order.delivery_slot.replace("|", " · ")}
                </div>
              )}

              <DeliveryLinks order={order} />
            </div>
            )}
          </div>

          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Payment</SectionTitle>
            <div className="mt-2 space-y-1 text-sm">
              <Row label="Subtotal" value={inr(subtotal)} />
              {editing ? (
                <label className="block text-xs text-hyd-text2">
                  Delivery fee
                  <Input type="number" min="0" step="0.01" value={draft.delivery_fee} onChange={(e) => setDraft((current) => ({ ...current, delivery_fee: e.target.value }))} className="mt-1 h-8 bg-hyd-bg border-hyd-border text-hyd-text" />
                </label>
              ) : <Row label="Delivery" value={order.delivery_fee === 0 ? "FREE" : inr(order.delivery_fee)} />}
              {discount > 0 && <Row label={`Coupon (${editing ? draft.coupon_code || "" : order.coupon_code || ""})`} value={`- ${inr(discount)}`} />}
              {editing && (
                <div className="pt-2">
                  {couponApplied && discount > 0 && (
                    <div className="mb-2 flex items-center justify-between gap-2 rounded-lg border border-emerald-800 bg-emerald-950/30 p-2 text-xs text-emerald-200">
                      <span><strong>{couponApplied.code}</strong> applied · save {inr(discount)}</span>
                      <Button type="button" variant="ghost" onClick={removeCoupon} disabled={saving} className="h-7 px-2 text-emerald-300 hover:text-white">
                        Remove
                      </Button>
                    </div>
                  )}
                  {!couponEntryOpen ? (
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => { setCouponEntryOpen(true); setCouponError(""); }}
                      disabled={saving}
                      className="w-full bg-hyd-panel2 border border-hyd-border text-hyd-gold"
                    >
                      {couponApplied ? "Change Coupon" : "Apply Coupon"}
                    </Button>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex gap-2">
                        <Input
                          aria-label="Coupon code"
                          placeholder="Enter coupon code"
                          value={couponCode}
                          onChange={(event) => setCouponCode(event.target.value.toUpperCase())}
                          className="bg-hyd-bg border-hyd-border text-hyd-text font-mono uppercase"
                        />
                        <Button type="button" onClick={applyCoupon} disabled={checkingCoupon || saving || !couponCode.trim()} className="bg-hyd-gold text-hyd-bg hover:bg-hyd-goldDim">
                          {checkingCoupon ? "Checking..." : "Apply"}
                        </Button>
                      </div>
                      {couponError && <div className="text-xs text-hyd-red">{couponError}</div>}
                    </div>
                  )}
                </div>
              )}
              <div className="border-t border-hyd-border my-1" />
              <Row label="Total" value={inr(total)} bold />
              <div className="text-[10px] uppercase tracking-widest text-hyd-text2 mt-2">Method: {order.payment_method}</div>
            </div>
          </div>

          <div className="bg-hyd-panel border border-hyd-border rounded-2xl p-3">
            <SectionTitle>Actions</SectionTitle>
            <div className="mt-2">
              <Label className="text-[10px] uppercase text-hyd-text2 tracking-widest">Change status</Label>
              <Select value={order.status} onValueChange={(v) => onStatus(order.id, v)} disabled={editing || saving}>
                <SelectTrigger data-testid={ADMIN.orderStatusSelect(order.id)} className="bg-hyd-bg border-hyd-border mt-1 rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NEXT_STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="text-[10px] uppercase tracking-widest text-hyd-gold font-bold">{children}</div>;
}

function DeliveryLinks({ order }) {
  const a = order.address || {};
  const hasCoords = a.lat != null && a.lng != null;
  const mapsUrl = hasCoords
    ? `https://www.google.com/maps?q=${a.lat},${a.lng}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ")
      )}`;
  const items = (order.items || []).map((i) => `• ${i.name} × ${i.qty}`).join("\n");
  const message = [
    `*Hyderabad Crackers — Delivery Task*`,
    ``,
    `*Order:* ${order.order_number}`,
    `*Customer:* ${a.name} (${a.phone})`,
    `*Address:* ${a.line1}${a.line2 ? ", " + a.line2 : ""}, ${a.city}, ${a.state} - ${a.pincode}`,
    a.landmark ? `*Landmark:* ${a.landmark}` : null,
    hasCoords ? `*GPS:* ${a.lat}, ${a.lng}` : null,
    `*Map:* ${mapsUrl}`,
    ``,
    `*Items:*\n${items}`,
    ``,
    `*Total:* ₹${order.total} (${order.payment_method})`,
    order.notes ? `*Notes:* ${order.notes}` : null,
  ].filter(Boolean).join("\n");
  const waUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;

  return (
    <div className="mt-3 pt-3 border-t border-hyd-border/60 space-y-2">
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-hyd-gold font-bold">
        <MapPin className="w-3 h-3" /> Delivery handoff
      </div>
      {hasCoords ? (
        <div className="text-[11px] text-hyd-text3">
          GPS: {Number(a.lat).toFixed(5)}, {Number(a.lng).toFixed(5)}
        </div>
      ) : (
        <div className="text-[11px] text-hyd-text3">No GPS captured — using address search</div>
      )}
      <div className="flex flex-wrap gap-2">
        <a
          href={mapsUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-order-maps-${order.id}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-hyd-bg border border-hyd-gold/60 text-hyd-gold rounded-full px-3 py-1.5 hover:bg-hyd-gold/10"
        >
          <ExternalLink className="w-3 h-3" /> Open in Maps
        </a>
        <a
          href={waUrl}
          target="_blank"
          rel="noreferrer"
          data-testid={`admin-order-whatsapp-${order.id}`}
          className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest bg-emerald-500 text-black rounded-full px-3 py-1.5 hover:bg-emerald-400"
        >
          <Share2 className="w-3 h-3" /> Share on WhatsApp
        </a>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className={`flex justify-between ${bold ? "font-bold text-base" : "text-sm"}`}>
      <span className="text-hyd-text2">{label}</span>
      <span>{value}</span>
    </div>
  );
}
